import React, { useState } from 'react';
import { useApp } from '../store';
import { UserRole, User, ProjectStage } from '../types';
import { Plus, Shield, Trash2, X, Lock, User as UserIcon, BarChart3, Briefcase, AlertCircle } from 'lucide-react';

const AddUserModal = ({ onClose }: { onClose: () => void }) => {
  const { addUser } = useApp();
  const [formData, setFormData] = useState({ name: '', password: '', role: UserRole.DESIGNER });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addUser({ id: `u-${Date.now()}`, ...formData, avatar: `https://picsum.photos/seed/${formData.name}/100/100` });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl p-8 animate-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between mb-8">
          <div><h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Invite Team Member</h2></div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400"><X className="w-6 h-6" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5">
          <input required type="text" placeholder="Full Name" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 font-bold" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
          <input required type="password" placeholder="Set Password" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 font-bold" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} />
          <select className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 font-black text-[10px] uppercase tracking-widest" value={formData.role} onChange={e => setFormData({...formData, role: e.target.value as UserRole})}>
            <option value={UserRole.DESIGNER}>Designer</option>
            <option value={UserRole.DEV_MANAGER}>Dev Manager</option>
            <option value={UserRole.QA_ENGINEER}>QA Engineer</option>
            <option value={UserRole.ADMIN}>Administrator</option>
          </select>
          <button type="submit" className="w-full py-4 bg-indigo-600 text-white font-black text-xs uppercase tracking-widest rounded-xl shadow-xl hover:bg-indigo-700 transition-all">Invite & Create</button>
        </form>
      </div>
    </div>
  );
};

const TeamMembers = () => {
  const { users, projects, deleteUser, currentUser } = useApp();
  const [showAdd, setShowAdd] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  const handleDelete = (userId: string) => {
    deleteUser(userId);
    setUserToDelete(null);
  };

  return (
    <div className="space-y-12 animate-in fade-in duration-500 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tight uppercase italic">Team Management</h2>
          <p className="text-slate-500 font-bold text-xs uppercase tracking-widest mt-1">Resource planning & team management</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-black text-xs uppercase tracking-widest rounded-xl shadow-lg hover:bg-indigo-700 transition-all">
          <Plus className="w-5 h-5" /> Add Team Member
        </button>
      </div>

      <div className="bg-white rounded-[2rem] border border-slate-200 overflow-hidden shadow-sm">
        <div className="px-8 py-6 border-b border-slate-100 bg-slate-50/50">
          <h3 className="text-sm font-black text-slate-700 uppercase tracking-widest">Team Details</h3>
        </div>
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-white border-b border-slate-100">
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Team Member</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Specialization</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Active Projects</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Delete Account</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map(user => {
              const count = projects.filter(p => p.stage !== ProjectStage.COMPLETED && (p.assignedDesignerId === user.id || p.assignedDevManagerId === user.id)).length;
              return (
                <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-3">
                      <img src={user.avatar} className="w-10 h-10 rounded-full border border-slate-200 shadow-sm" alt="" />
                      <span className="font-black text-slate-900 tracking-tight italic">{user.name}</span>
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <span className="text-[10px] font-black text-indigo-600 bg-indigo-50 px-3 py-1 rounded-lg uppercase tracking-widest">{user.role}</span>
                  </td>
                  <td className="px-8 py-5 text-center">
                    <span className="text-lg font-black text-slate-900">{count}</span>
                  </td>
                  <td className="px-8 py-5 text-right">
                    {user.id !== currentUser?.id && (
                      <button onClick={() => setUserToDelete(user)} className="p-2.5 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"><Trash2 className="w-4.5 h-4.5" /></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showAdd && <AddUserModal onClose={() => setShowAdd(false)} />}
      {userToDelete && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm rounded-[2rem] shadow-2xl p-8 text-center animate-in zoom-in-95">
            <Trash2 className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-black text-slate-900 uppercase italic mb-2">Delete Account?</h3>
            <p className="text-sm text-slate-500 mb-8">Confirm permanent removal of <span className="font-black text-slate-900">{userToDelete.name}</span> from the agency roster.</p>
            <div className="flex gap-4">
              <button onClick={() => setUserToDelete(null)} className="flex-1 py-3 bg-slate-100 font-black rounded-xl text-[10px] uppercase tracking-widest">Cancel</button>
              <button onClick={() => handleDelete(userToDelete.id)} className="flex-1 py-3 bg-red-600 text-white font-black rounded-xl text-[10px] uppercase tracking-widest">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamMembers;